import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Save, FileText, MessageSquare, Star, Plus, Users, User, Briefcase, Building2, ChevronDown, CheckCircle2, Clock, XCircle } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import ImageUploader from "../../components/ImageUploader";

const PROVINCES = ["Alberta", "Colombie-Britannique", "Île-du-Prince-Édouard", "Manitoba", "Nouveau-Brunswick", "Nouvelle-Écosse", "Nunavut", "Ontario", "Québec", "Saskatchewan", "Terre-Neuve-et-Labrador", "Territoires du Nord-Ouest", "Yukon"];
const TYPES = [
  { value: "professional", label: "Professionnel", Icon: User, desc: "Une fiche mettant en avant vos services et compétences." },
  { value: "entrepreneur", label: "Entrepreneur", Icon: Briefcase, desc: "Une fiche pour votre entreprise ou start-up." },
  { value: "organism", label: "Organisme", Icon: Building2, desc: "Une fiche pour un OBNL, un centre d'accueil ou une association." },
];

const ACTIVE_PROFILE_KEY = "demka_active_profile";

const STATUS_STYLE = {
  approved: { bg: "bg-emerald-100", text: "text-emerald-700", label: "Approuvé", Icon: CheckCircle2 },
  pending: { bg: "bg-amber-100", text: "text-amber-700", label: "En attente", Icon: Clock },
  rejected: { bg: "bg-red-100", text: "text-red-700", label: "Refusé", Icon: XCircle },
};

function profileTypeMeta(type) { return TYPES.find((t) => t.value === type) || TYPES[0]; }

export default function ProviderDashboard() {
  const { user } = useAuth();
  const [profiles, setProfiles] = useState([]);
  const [activeId, setActiveId] = useState(() => localStorage.getItem(ACTIVE_PROFILE_KEY) || null);
  const [categories, setCategories] = useState([]);
  const [requests, setRequests] = useState([]);
  const [creatingNew, setCreatingNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [switcherOpen, setSwitcherOpen] = useState(false);

  const [form, setForm] = useState(emptyForm(user?.email));

  const activeProfile = useMemo(
    () => profiles.find((p) => p.id === activeId) || null,
    [profiles, activeId],
  );

  // Load categories once
  useEffect(() => { api.get("/categories").then((r) => setCategories(r.data)); }, []);

  // Load all owned profiles + pick active
  const loadProfiles = async () => {
    const { data } = await api.get("/my/profiles");
    setProfiles(data);
    if (data.length === 0) {
      setActiveId(null);
      setCreatingNew(true);
      setForm(emptyForm(user?.email));
    } else {
      const stored = localStorage.getItem(ACTIVE_PROFILE_KEY);
      const chosen = data.find((p) => p.id === stored) || data[0];
      setActiveId(chosen.id);
      setForm({ ...emptyForm(user?.email), ...chosen });
      setCreatingNew(false);
    }
    return data;
  };
  useEffect(() => { loadProfiles(); /* eslint-disable-next-line */ }, []);

  // Load requests for the active profile (or all when none active)
  useEffect(() => {
    const params = activeId ? { profile_id: activeId } : {};
    api.get("/provider/requests", { params }).then((r) => setRequests(r.data)).catch(() => {});
  }, [activeId]);

  const selectProfile = (p) => {
    setActiveId(p.id);
    localStorage.setItem(ACTIVE_PROFILE_KEY, p.id);
    setForm({ ...emptyForm(user?.email), ...p });
    setCreatingNew(false);
    setSwitcherOpen(false);
  };

  const startNew = (type) => {
    setActiveId(null);
    setCreatingNew(true);
    setForm({ ...emptyForm(user?.email), profile_type: type || "professional" });
    setSwitcherOpen(false);
  };

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleArr = (k, v) => setForm((f) => ({ ...f, [k]: f[k]?.includes(v) ? f[k].filter((x) => x !== v) : [...(f[k] || []), v] }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      ["id", "owner_user_id", "status", "featured", "avg_rating", "review_count", "created_at", "reviews", "is_demo"].forEach((k) => delete payload[k]);
      if (creatingNew) {
        const { data } = await api.post("/profiles", payload);
        toast.success("Nouveau profil créé — en attente de validation admin.");
        await loadProfiles();
        setActiveId(data.id);
        localStorage.setItem(ACTIVE_PROFILE_KEY, data.id);
        setCreatingNew(false);
      } else if (activeProfile?.id) {
        const { data } = await api.put(`/profiles/${activeProfile.id}`, payload);
        toast.success("Profil mis à jour — en attente de re-validation.");
        setProfiles((ps) => ps.map((p) => (p.id === data.id ? data : p)));
        setForm({ ...emptyForm(user?.email), ...data });
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="provider-dashboard">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-6">
        <div>
          <p className="text-xs uppercase tracking-widest text-primary font-semibold mb-1">Espace prestataire</p>
          <h1 className="font-display font-bold text-3xl sm:text-4xl tracking-tight">Bonjour {user?.name}</h1>
          <p className="text-muted-foreground text-sm mt-1">Gérez toutes vos casquettes depuis un seul compte.</p>
          <Link
            to="/contact?prefill=role_change"
            data-testid="request-role-change-btn"
            className="inline-flex items-center gap-2 mt-3 px-3 py-1.5 rounded-full bg-white border border-primary/20 text-primary hover:bg-primary hover:text-white transition text-xs font-medium"
            title="Demander un changement de rôle à l'équipe DemkaBiz"
          >
            Demander un changement de rôle
          </Link>
        </div>

        {/* Profile switcher */}
        {profiles.length > 0 && (
          <div className="relative" data-testid="profile-switcher">
            <button
              onClick={() => setSwitcherOpen((v) => !v)}
              className="inline-flex items-center gap-3 px-4 py-3 rounded-2xl bg-white border border-primary/15 shadow-sm hover:border-primary transition min-w-[280px]"
              data-testid="profile-switcher-btn"
            >
              <Users className="w-5 h-5 text-primary flex-shrink-0" />
              <div className="text-left flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
                  {creatingNew ? "Nouveau profil" : "Voir en tant que"}
                </div>
                <div className="font-medium text-sm truncate">
                  {creatingNew ? `Créer un nouveau ${profileTypeMeta(form.profile_type).label.toLowerCase()}` : (activeProfile?.name || "—")}
                </div>
              </div>
              <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${switcherOpen ? "rotate-180" : ""}`} />
            </button>
            {switcherOpen && (
              <div className="absolute right-0 mt-2 w-80 bg-white border border-primary/15 rounded-2xl shadow-lg overflow-hidden z-40" data-testid="profile-switcher-menu">
                <div className="p-2 max-h-80 overflow-y-auto">
                  {profiles.map((p) => {
                    const meta = profileTypeMeta(p.profile_type);
                    const st = STATUS_STYLE[p.status] || STATUS_STYLE.pending;
                    return (
                      <button
                        key={p.id}
                        onClick={() => selectProfile(p)}
                        data-testid={`switch-to-${p.id}`}
                        className={`w-full flex items-start gap-3 p-3 rounded-xl text-left transition ${p.id === activeId && !creatingNew ? "bg-secondary" : "hover:bg-secondary/60"}`}
                      >
                        <meta.Icon className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm truncate">{p.name || "(Sans nom)"}</div>
                          <div className="text-xs text-muted-foreground">{meta.label}</div>
                        </div>
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${st.bg} ${st.text} flex-shrink-0`}>
                          <st.Icon className="w-3 h-3" /> {st.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
                <div className="border-t border-primary/10 p-2 bg-secondary/40">
                  <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold px-2 pt-1 pb-2">Ajouter une casquette</p>
                  {TYPES.map((t) => (
                    <button
                      key={t.value}
                      onClick={() => startNew(t.value)}
                      data-testid={`add-profile-${t.value}`}
                      className="w-full flex items-center gap-3 p-2.5 rounded-xl text-left hover:bg-white transition"
                    >
                      <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
                        <Plus className="w-4 h-4" />
                      </div>
                      <div className="flex-1">
                        <div className="text-sm font-medium">{t.label}</div>
                        <div className="text-xs text-muted-foreground">{t.desc}</div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Status badge for active profile */}
      {!creatingNew && activeProfile && (
        (() => {
          const st = STATUS_STYLE[activeProfile.status] || STATUS_STYLE.pending;
          return (
            <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium uppercase tracking-wider mb-6 ${st.bg} ${st.text}`}>
              <st.Icon className="w-3.5 h-3.5" /> Statut : {st.label}
            </div>
          );
        })()
      )}
      {creatingNew && (
        <div className="rounded-2xl border-2 border-dashed border-primary/30 bg-secondary/30 p-4 mb-6 flex items-start gap-3" data-testid="new-profile-notice">
          <Plus className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-medium">Vous créez un nouveau profil de type <strong className="text-primary">{profileTypeMeta(form.profile_type).label.toLowerCase()}</strong>.</p>
            <p className="text-muted-foreground mt-1">Une fois enregistré, il sera soumis à validation par l'équipe DemkaBiz. Vous pourrez basculer entre vos casquettes depuis le sélecteur en haut à droite.</p>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        <form onSubmit={save} className="card-surface p-6 lg:col-span-2 space-y-4" data-testid="provider-profile-form">
          <h2 className="font-display font-bold text-xl">{creatingNew ? "Nouvelle fiche" : "Ma fiche"}</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            <Input label="Nom / Organisation" value={form.name} onChange={(v) => update("name", v)} required testid="prov-name" />
            <Select label="Type" value={form.profile_type} onChange={(v) => update("profile_type", v)} options={TYPES.map((t) => [t.value, t.label])} testid="prov-type" disabled={!creatingNew} />
          </div>
          <ImageUploader value={form.photo_url} onChange={(v) => update("photo_url", v)} testid="prov-photo" label="Photo / Logo du profil" />
          <div className="grid sm:grid-cols-2 gap-3">
            <Input label="Site web" value={form.website} onChange={(v) => update("website", v)} testid="prov-website" />
            <Select label="Province" value={form.province} onChange={(v) => update("province", v)} options={[["", "—"], ...PROVINCES.map((p) => [p, p])]} testid="prov-province" />
            <Input label="Ville" value={form.city} onChange={(v) => update("city", v)} testid="prov-city" />
            <Input label="Téléphone" value={form.phone} onChange={(v) => update("phone", v)} testid="prov-phone" />
            <Input label="WhatsApp (avec indicatif)" value={form.whatsapp} onChange={(v) => update("whatsapp", v)} testid="prov-wa" />
            <Input label="Email public" type="email" value={form.email} onChange={(v) => update("email", v)} testid="prov-email" />
          </div>
          <div>
            <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Description</label>
            <textarea rows={4} value={form.description} onChange={(e) => update("description", e.target.value)} className="w-full mt-1 px-4 py-3 border border-emerald-900/10 rounded-xl text-sm outline-none focus:border-primary" data-testid="prov-desc" />
          </div>

          {/* Attributs prestataire (PEIF pancanadien + Québec-registered) */}
          <div className="rounded-2xl bg-secondary/40 border border-primary/10 p-4">
            <p className="text-xs uppercase tracking-widest text-primary font-semibold mb-3">Attributs prestataire</p>
            <div className="grid sm:grid-cols-2 gap-3">
              <label className="flex items-start gap-3 p-3 rounded-xl bg-white border border-primary/10 cursor-pointer hover:border-primary/40 transition">
                <input type="checkbox" checked={!!form.peif} onChange={(e) => update("peif", e.target.checked)} data-testid="prov-peif" className="mt-1 accent-primary" />
                <span>
                  <span className="text-sm font-medium block">Je suis passé(e) par le PEIF</span>
                  <span className="text-xs text-muted-foreground">Parcours entrepreneurial pour immigrants francophones — Collège La Cité (Ontario)</span>
                </span>
              </label>
              <label className="flex items-start gap-3 p-3 rounded-xl bg-white border border-primary/10 cursor-pointer hover:border-primary/40 transition">
                <input type="checkbox" checked={!!form.quebec_incorporated} onChange={(e) => update("quebec_incorporated", e.target.checked)} data-testid="prov-quebec-inc" className="mt-1 accent-primary" />
                <span>
                  <span className="text-sm font-medium block">Constitué(e) au Québec</span>
                  <span className="text-xs text-muted-foreground">Entreprise / organisme immatriculé au Registre des entreprises du Québec (REQ) — affiche l'étiquette « Ici Québec »</span>
                </span>
              </label>
            </div>
          </div>

          <div>
            <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Langues parlées</label>
            <div className="flex flex-wrap gap-2 mt-2">
              {["Français", "Anglais", "Wolof", "Pulaar", "Arabe", "Lingala", "Bambara", "Espagnol"].map((l) => (
                <button type="button" key={l} onClick={() => toggleArr("languages", l)}
                  data-testid={`prov-lang-${l}`}
                  className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${form.languages?.includes(l) ? "bg-primary text-white border-primary" : "border-emerald-900/15 hover:border-primary"}`}>
                  {l}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Catégories de services</label>
            <div className="flex flex-wrap gap-2 mt-2">
              {categories.map((c) => (
                <button type="button" key={c.slug} onClick={() => toggleArr("categories", c.slug)}
                  data-testid={`prov-cat-${c.slug}`}
                  className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${form.categories?.includes(c.slug) ? "bg-primary text-white border-primary" : "border-emerald-900/15 hover:border-primary"}`}>
                  {c.name}
                </button>
              ))}
            </div>
          </div>
          <button type="submit" disabled={saving} className="btn-primary" data-testid="prov-save">
            <Save className="w-4 h-4" /> {saving ? "Enregistrement…" : creatingNew ? "Créer la fiche" : "Mettre à jour"}
          </button>
        </form>

        <div className="lg:col-span-1 space-y-4">
          <div className="card-surface p-5">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground">Demandes reçues</div>
                <div className="font-display font-bold text-3xl mt-1">{requests.length}</div>
                <div className="text-[10px] text-muted-foreground mt-0.5">{creatingNew ? "après création" : activeProfile ? "sur cette fiche" : "toutes fiches"}</div>
              </div>
              <FileText className="w-8 h-8 text-primary" />
            </div>
          </div>
          <div className="card-surface p-5">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground">Note moyenne</div>
                <div className="font-display font-bold text-3xl mt-1">{activeProfile?.avg_rating?.toFixed?.(1) || "—"}</div>
              </div>
              <Star className="w-8 h-8 text-amber-400 fill-amber-400" />
            </div>
          </div>
          <div className="card-surface p-5">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground">Avis reçus</div>
                <div className="font-display font-bold text-3xl mt-1">{activeProfile?.review_count || 0}</div>
              </div>
              <MessageSquare className="w-8 h-8 text-primary" />
            </div>
          </div>
          {profiles.length > 1 && (
            <div className="card-surface p-5 bg-primary/5 border-primary/20">
              <div className="flex items-center gap-2 text-primary mb-2">
                <Users className="w-4 h-4" />
                <p className="text-xs font-semibold uppercase tracking-wider">Multi-casquettes</p>
              </div>
              <p className="text-sm text-foreground/85">
                Vous gérez <strong>{profiles.length} fiches</strong> depuis ce compte. Basculez à tout moment via le sélecteur en haut.
              </p>
            </div>
          )}
        </div>
      </div>

      {!creatingNew && (
        <div className="card-surface mt-8" data-testid="provider-requests-section">
          <div className="p-5 border-b border-emerald-900/10 flex items-center justify-between">
            <h2 className="font-display font-semibold text-lg">Demandes reçues</h2>
            {activeProfile && <span className="text-xs text-muted-foreground">pour <strong className="text-foreground">{activeProfile.name}</strong></span>}
          </div>
          <div className="divide-y divide-emerald-900/5">
            {requests.length === 0 ? (
              <div className="p-10 text-center text-muted-foreground text-sm">Aucune demande pour le moment.</div>
            ) : requests.map((r) => (
              <div key={r.id} className="p-5" data-testid={`prov-request-${r.id}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="font-medium">{r.name}</span>
                  <span className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleDateString("fr-CA")}</span>
                </div>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">{r.description}</p>
                <div className="text-xs mt-2">{r.email} · {r.phone}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function emptyForm(email) {
  return {
    name: "", profile_type: "professional",
    photo_url: "", province: "", city: "", languages: [], categories: [],
    description: "", phone: "", email: email || "", whatsapp: "", website: "",
  };
}

function Input({ label, value, onChange, type = "text", required, testid }) {
  return (
    <div>
      <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{label}</label>
      <input type={type} required={required} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full mt-1 px-4 py-3 border border-emerald-900/10 rounded-xl text-sm outline-none focus:border-primary" />
    </div>
  );
}
function Select({ label, value, onChange, options, testid, disabled }) {
  return (
    <div>
      <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{label}</label>
      <select value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} disabled={disabled}
        className="w-full mt-1 px-4 py-3 border border-emerald-900/10 rounded-xl text-sm outline-none focus:border-primary disabled:opacity-70 disabled:cursor-not-allowed">
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );
}
